import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, Download, Loader2, AlertTriangle } from 'lucide-react';

interface PDFViewerProps {
  pdfUrl: string;
  title?: string;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, title = "Visualizar Documento" }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);

  useEffect(() => {
    loadPDF();
  }, [pdfUrl]);

  const loadPDF = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Carregando PDF de:', pdfUrl);
      
      const response = await fetch(pdfUrl);
      
      if (!response.ok) {
        throw new Error(`Erro ao carregar PDF: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const dataUrl = URL.createObjectURL(blob);
      
      console.log('PDF carregado com sucesso');
      setPdfDataUrl(dataUrl);
    } catch (err) {
      console.error('Erro ao carregar PDF:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (pdfDataUrl) {
      const link = document.createElement('a');
      link.href = pdfDataUrl;
      link.download = 'documento.pdf';
      link.click();
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfDataUrl) {
      window.open(pdfDataUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Eye className="w-5 h-5 mr-2" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
              <p>Carregando documento...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Eye className="w-5 h-5 mr-2" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              {error}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadPDF}
                className="ml-2"
              >
                Tentar Novamente
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Visualização do PDF - Tela maior */}
      <div className="border rounded-lg overflow-hidden bg-gray-50">
        <iframe
          src={`${pdfDataUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          className="w-full h-[600px] md:h-[700px] lg:h-[800px]"
          title="Visualização do PDF"
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
      
      {/* Botões de ação */}
      <div className="flex gap-2 justify-center">
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenInNewTab}
        >
          <Eye className="w-4 h-4 mr-2" />
          Abrir em Nova Aba
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
        >
          <Download className="w-4 h-4 mr-2" />
          Baixar PDF
        </Button>
      </div>
    </div>
  );
};
